import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = { percent: "Pourcentage (%)", fixed: "Montant fixe (CHF)", bogo: "Acheté = Offert (BOGO)" };

export default function PromosPanel() {
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [p, pr] = await Promise.all([api.get("/admin/promos"), api.get("/admin/products")]);
      setList(p.data);
      setProducts(pr.data);
    } catch { /* products may be 403 for staff */ try { const { data } = await api.get("/admin/promos"); setList(data); } catch {} }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.type === "bogo" && !editing.product_id) {
        toast.error("BOGO : sélectionnez un produit"); return;
      }
      const payload = {
        code: editing.code.toUpperCase(),
        type: editing.type,
        value: Number(editing.value || 0),
        min_order: Number(editing.min_order || 0),
        active: !!editing.active,
        product_id: editing.product_id || null,
      };
      if (editing.id) await api.put(`/admin/promos/${editing.id}`, payload);
      else await api.post("/admin/promos", payload);
      toast.success("Enregistré"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const del = async (id) => { if (window.confirm("Supprimer ?")) { await api.delete(`/admin/promos/${id}`); load(); } };

  const productName = (id) => products.find(p => p.id === id)?.name || id;

  const describe = (p) => {
    if (p.type === "bogo") return `1 acheté = 1 offert sur « ${productName(p.product_id)} »`;
    if (p.type === "percent") return p.product_id ? `-${p.value}% sur ${productName(p.product_id)}` : `-${p.value}% sur le total`;
    return `-${p.value.toFixed(2)} CHF sur le total`;
  };

  return (
    <div data-testid="promos-panel">
      <h1 className="font-display text-4xl mb-6">Codes promo</h1>
      <button onClick={() => setEditing({ code: "", type: "percent", value: 10, min_order: 0, active: true, product_id: null })} data-testid="add-promo-btn" className="bg-primary text-white px-4 py-2 rounded-sm mb-4 flex items-center gap-2 hover:bg-primary/90">
        <Plus size={16} /> Nouveau code
      </button>

      <div className="space-y-2 max-w-3xl">
        {list.length === 0 && <div className="text-muted-foreground">Aucun code promo.</div>}
        {list.map(p => (
          <div key={p.id} data-testid={`promo-${p.code}`} className="bg-white border border-border rounded-sm p-4 flex justify-between items-center">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono font-semibold text-primary text-lg">{p.code}</span>
                <span className="text-[10px] uppercase tracking-wider bg-accent text-primary px-1.5 py-0.5 rounded-sm">{TYPE_LABELS[p.type]}</span>
                {!p.active && <span className="text-[10px] uppercase tracking-wider bg-destructive/10 text-destructive px-1.5 py-0.5 rounded-sm">Inactif</span>}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {describe(p)}{p.min_order > 0 && ` · min ${p.min_order.toFixed(2)} CHF`}
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setEditing({ ...p })} className="text-primary text-sm hover:underline">Éditer</button>
              <button onClick={() => del(p.id)} className="text-destructive text-sm hover:underline"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing?.id ? "Modifier" : "Nouveau"} code promo</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Field label="Code"><Input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} data-testid="promo-code" /></Field>
              <Field label="Type">
                <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v, value: v === "bogo" ? 0 : (editing.value || 10) })}>
                  <SelectTrigger data-testid="promo-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Pourcentage (%)</SelectItem>
                    <SelectItem value="fixed">Montant fixe (CHF)</SelectItem>
                    <SelectItem value="bogo">Acheté = Offert (BOGO)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {editing.type !== "bogo" && (
                <Field label={editing.type === "percent" ? "Pourcentage (ex 10)" : "Montant (CHF)"}><Input type="number" step="0.5" value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} data-testid="promo-value" /></Field>
              )}
              {(editing.type === "bogo" || editing.type === "percent") && products.length > 0 && (
                <Field label={editing.type === "bogo" ? "Produit concerné (obligatoire)" : "Limiter à un produit (optionnel)"}>
                  <Select value={editing.product_id || "_all"} onValueChange={(v) => setEditing({ ...editing, product_id: v === "_all" ? null : v })}>
                    <SelectTrigger data-testid="promo-product"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {editing.type !== "bogo" && <SelectItem value="_all">— Tout le panier —</SelectItem>}
                      {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              )}
              <Field label="Montant minimum (CHF)"><Input type="number" step="0.5" value={editing.min_order} onChange={(e) => setEditing({ ...editing, min_order: e.target.value })} /></Field>
              <label className="flex items-center gap-3"><Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /> Actif</label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="px-4 py-2 border border-border rounded-sm">Annuler</button>
            <button onClick={save} data-testid="save-promo-btn" className="px-4 py-2 bg-primary text-white rounded-sm">Enregistrer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">{label}</Label>{children}</div>;
}
